import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosPrivate from '../../../config/axiosPrivate';
import WebsiteFilterAutocomplete from './WebsiteFilterAutocomplete';


export const WebsiteFilter = ({
  websitesList = [],
  selectedWebsiteUrl,
  setSelectedWebsiteUrl,
  onAddWebsite = null,
  onEditWebsite = null,
  onDeleteWebsite = null,
  storageKey = 'AC_Dashboard_SelectedWebsite',
  placeholder = null,
  className = '',
  style = {},
}) => {
  const { t } = useTranslation();
  const [internalList, setInternalList] = useState([]);

  const parentHasSites =
    Array.isArray(websitesList) && websitesList.length > 0;
  const parentSitesKey = parentHasSites
    ? websitesList.map((c) => c?.id ?? '').join('|')
    : 'no-parent-sites';

  // Charger la liste en interne si le parent ne fournit pas de sites
  useEffect(() => {
    if (parentHasSites) return;

    let mounted = true;
    (async () => {
      try {
        const res = await axiosPrivate.get('/project/website/all');
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!mounted) return;
        setInternalList(list);
        try {
          const minimal = list.map((c) => ({ id: c.id, urlSite: c.urlSite || c.url || c.name || c.website?.urlSite })).filter((x) => !!x.urlSite);
          window.localStorage.setItem('AC_Websites_Cache', JSON.stringify(minimal));
        } catch { }
        try {
          window.dispatchEvent(new CustomEvent('websiteListLoaded', { detail: { source: 'WebsiteFilter' } }));
        } catch { }
      } catch (e) {
        setInternalList([]);
        try {
          window.localStorage.removeItem('AC_Websites_Cache');
          window.dispatchEvent(new CustomEvent('websiteListLoaded', { detail: { source: 'WebsiteFilter', error: true } }));
        } catch { }
      }
    })();

    return () => { mounted = false; };
  }, [parentHasSites, parentSitesKey]);

  const rawList = useMemo(() => {
    if (Array.isArray(websitesList) && websitesList.length > 0) return websitesList;
    return internalList;
  }, [websitesList, internalList]);

  const websitesOptions = useMemo(() => (rawList || [])
    .filter(Boolean)
    .map((c) => ({
      id: c.id,
      urlSite: c.urlSite || c.url || c.name || c.website?.urlSite
    }))
    .filter((x) => !!x.urlSite), [rawList]);

  const hasSites = websitesOptions.length > 0;
  const canAdd = Boolean(onAddWebsite);

  // ⚠️ Option spéciale "Ajouter"
  const ADD_OPTION = { __add__: true, id: '__add__', urlSite: t('Ajouter un nouveau site web') };

  // ⚙️ Liste à afficher même si vide (on montre l'option "Ajouter")
  const listWithAddOption = canAdd
    ? [...websitesOptions, ADD_OPTION]
    : websitesOptions;

  const selected = websitesOptions.find((o) => o.urlSite === selectedWebsiteUrl) || null;

  const handleSetValue = (option) => {
    // Si clic sur l’option "Ajouter"
    if (option?.__add__ && onAddWebsite) {
      onAddWebsite();
      return;
    }

    const value = option?.urlSite || '';
    try {
      if (value) {
        window.localStorage.setItem(storageKey, value);
      } else {
        window.localStorage.removeItem(storageKey);
      }
    } catch { }
    setSelectedWebsiteUrl(value);
  };

  return (
    <div
      id="website-filter-container"
      style={{ marginLeft: 8 }}
    >
      <WebsiteFilterAutocomplete
        disabled={!hasSites && !canAdd}
        className={className}
        style={{ minWidth: 280, maxWidth: 500, ...style }}
        list={listWithAddOption}
        value={selected}
        setValue={handleSetValue}
        getOptionDisabled={() => false}
        getOptionLabel={(option) => {
          if (option?.__add__) return t('Ajouter un nouveau site web');
          return option?.urlSite || '';
        }}
        placeholder={
          placeholder ||
          (hasSites
            ? t('Sélectionner le site web')
            : canAdd
              ? t('Aucun site — ajouter un nouveau')
              : t('Aucun site disponible'))
        }
        renderOptionLabel={(option) => {
          // Option spéciale "Add new website"
          if (option?.__add__ && onAddWebsite) {
            return t('Ajouter un nouveau site web');
          }
          return option?.urlSite || '';
        }}
        renderOptionEnd={(option) => {
          if (option?.__add__) return null;

          const hasActions = onEditWebsite || onDeleteWebsite;
          if (!hasActions) return null;

          return (
            <span style={{ display: 'inline-flex', gap: 8 }}>
              {onEditWebsite && (
                <img
                  src="Images/icon-edit.png"
                  alt="edit"
                  title={t('Modifier')}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEditWebsite(option);
                  }}
                />
              )}
              {onDeleteWebsite && (
                <img
                  src="Images/icon-trash.png"
                  alt="trash"
                  title={t('Supprimer')}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDeleteWebsite(option);
                  }}
                />
              )}
            </span>
          );
        }}
      />
    </div>
  );
};


export default WebsiteFilter;

